import { useNavigate } from 'react-router-dom'
import { ChevronLeft, ChevronRight, LogOut, X } from 'lucide-react'
import { AnimatePresence, motion } from 'framer-motion'
import { useAuthStore } from '@/stores/authStore'
import { useThemeStore } from '@/stores/themeStore'
import { useTheme } from '@/hooks/useTheme'
import { useUIStore } from '@/stores/uiStore'
import { cn } from '@/utils/cn'
import {
  HEADER_BAND_HEIGHT_PX,
  SIDEBAR_WIDTH_COLLAPSED,
  SIDEBAR_WIDTH_EXPANDED,
} from '@/constants/layout'
import {
  getCollapsedSidebarLogo,
  getExpandedSidebarLogo,
  getExpandedSidebarLogoFallback,
} from '@/components/layout/sidebarLogos'
import { NAV_SECTION_THEMES } from '@/components/layout/navIconStyles'
import { SidebarItem } from '@/components/layout/sidebarNavItem'
import { PARCEIROS_PATH } from './parceirosBrand'
import { PARCEIROS_NAV } from './parceirosNav'

function ParceirosSidebarNav({
  collapsed,
  onNavigate,
  isDark,
}: {
  collapsed: boolean
  onNavigate?: () => void
  isDark: boolean
}) {
  return (
    <nav className='relative z-10 flex-1 overflow-y-auto px-2 pb-4 pt-2 custom-scrollbar'>
      {PARCEIROS_NAV.map((section, index) => (
        <div key={section.label} className='mb-1'>
          {!collapsed && (
            <p
              className={cn(
                'mb-2 px-3 pt-5 text-[11px] font-semibold uppercase tracking-wide first:pt-0',
                section.themeKey !== 'financeiro' && 'text-text-muted',
              )}
              style={
                section.themeKey === 'financeiro'
                  ? { color: NAV_SECTION_THEMES.financeiro.color }
                  : undefined
              }
            >
              {section.label}
            </p>
          )}
          {collapsed && index > 0 && (
            <div
              className={cn('my-2 mx-2 border-t', isDark ? 'border-white/[0.06]' : 'border-[#eef2f7]')}
            />
          )}
          <ul className='space-y-0.5'>
            {section.items.map((item) => (
              <li key={item.to}>
                <SidebarItem
                  {...item}
                  sectionKey={section.themeKey}
                  collapsed={collapsed}
                  onNavigate={onNavigate}
                  isDark={isDark}
                />
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  )
}

function ParceirosSidebarShell({
  collapsed,
  onNavigate,
  onClose,
  showClose,
}: {
  collapsed: boolean
  onNavigate?: () => void
  onClose?: () => void
  showClose?: boolean
}) {
  const { theme } = useThemeStore()
  const { logout } = useAuthStore()
  const { isDark } = useTheme()
  const toggleSidebarCollapsed = useUIStore((s) => s.toggleSidebarCollapsed)
  const navigate = useNavigate()
  const collapsedLogo = getCollapsedSidebarLogo(theme.mode)
  const expandedLogo = getExpandedSidebarLogo(theme.mode, theme)
  const shellBorder = isDark ? 'border-white/[0.06]' : 'border-[#eef2f7]'

  async function handleLogout() {
    onNavigate?.()
    await logout()
    navigate(PARCEIROS_PATH, { replace: true })
  }

  return (
    <>
      <div
        className={cn(
          'relative z-10 flex shrink-0 border-b',
          shellBorder,
          collapsed
            ? 'flex-col items-center justify-center gap-1 px-1'
            : 'flex-row items-center justify-between gap-2 px-4',
        )}
        style={{ height: HEADER_BAND_HEIGHT_PX }}
      >
        <img
          src={collapsed ? collapsedLogo : expandedLogo}
          alt={theme.companyName}
          onError={(e) => {
            const img = e.target as HTMLImageElement
            img.src = collapsed
              ? getCollapsedSidebarLogo(theme.mode)
              : getExpandedSidebarLogoFallback(theme.mode)
          }}
          className={cn('object-contain', collapsed ? 'h-7 w-7' : 'h-6 w-auto max-w-[180px]')}
        />
        {showClose ? (
          <button
            type='button'
            onClick={onClose}
            className={cn(
              'flex h-11 w-11 shrink-0 items-center justify-center rounded-md transition-colors',
              isDark
                ? 'text-text-muted hover:bg-white/[0.06] hover:text-text-primary'
                : 'text-[#64748b] hover:bg-[#f1f5f9] hover:text-[#334155]',
            )}
            aria-label='Fechar menu'
          >
            <X size={18} />
          </button>
        ) : (
          <button
            type='button'
            onClick={toggleSidebarCollapsed}
            className={cn(
              'shrink-0 rounded-md p-1 transition-colors',
              isDark
                ? 'text-text-muted hover:bg-white/[0.06] hover:text-text-primary'
                : 'text-[#64748b] hover:bg-[#f1f5f9] hover:text-[#334155]',
            )}
            aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
            title={collapsed ? 'Expandir menu' : 'Recolher menu'}
          >
            {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={16} />}
          </button>
        )}
      </div>

      <ParceirosSidebarNav collapsed={collapsed} onNavigate={onNavigate} isDark={isDark} />

      <div className={cn('relative z-10 border-t py-3', shellBorder, collapsed ? 'px-1' : 'px-3')}>
        <button
          type='button'
          onClick={() => void handleLogout()}
          title='Sair'
          className={cn(
            'flex w-full items-center rounded-xl py-2 text-[13px] transition-colors',
            collapsed ? 'justify-center px-2' : 'gap-2.5 px-3',
            isDark
              ? 'text-text-muted hover:bg-danger/[0.06] hover:text-danger'
              : 'text-[#64748b] hover:bg-red-50 hover:text-red-600',
          )}
        >
          <LogOut size={16} />
          {!collapsed && 'Sair'}
        </button>
      </div>
    </>
  )
}

export function ParceirosSidebar() {
  const sidebarCollapsed = useUIStore((s) => s.sidebarCollapsed)
  const isMobileOpen = useUIStore((s) => s.isMobileOpen)
  const closeMobileMenu = useUIStore((s) => s.closeMobileMenu)
  const { isDark } = useTheme()
  const sidebarWidth = sidebarCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED
  const shellBorder = isDark ? 'border-white/[0.06]' : 'border-[#eef2f7]'

  return (
    <>
      <aside
        style={{ width: sidebarWidth }}
        className={cn(
          'hidden h-screen shrink-0 flex-col border-r bg-bg-sidebar transition-[width] duration-200 ease-out select-none md:flex',
          shellBorder,
        )}
      >
        <ParceirosSidebarShell collapsed={sidebarCollapsed} />
      </aside>

      <AnimatePresence>
        {isMobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={closeMobileMenu}
              className='fixed inset-0 z-40 bg-black/60 md:hidden'
            />
            <motion.aside
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className='fixed bottom-0 left-0 top-0 z-50 flex w-[17rem] flex-col border-r border-[#eef2f7] bg-bg-sidebar dark:border-white/[0.06] md:hidden'
            >
              <ParceirosSidebarShell
                collapsed={false}
                onNavigate={closeMobileMenu}
                onClose={closeMobileMenu}
                showClose
              />
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  )
}
